"use client";

import Image from "next/image";
import { ThemeToggle } from "@/components/theme-toggle";
import Link from "next/link";
import { useI18n } from "@/components/i18n-provider";

const iconMapPin = "/assets/icon-map-pin.svg";
const iconEnvelope = "/assets/icon-envelope.svg";
const iconInstagram = "/assets/icon-instagram.svg";

type ContactLink = {
  icon: string;
  label: string;
  href: string;
  external?: boolean;
};

/**
 * The email row points at /contact rather than a raw mailto (SEO-T01).
 * Cloudflare's Email Address Obfuscation rewrites every mailto href to
 * /cdn-cgi/l/email-protection, which returns 404 and is disallowed by
 * /cdn-cgi/ in robots.txt. This footer renders on every marketing page, so
 * that single link put a broken internal link on 222 of them. The address
 * stays visible as the label, and /contact carries the real mailto.
 */
const buildContactLinks = (locale: string): ContactLink[] => [
  {
    icon: iconMapPin,
    label: "Malang, Jawa Timur",
    href: "https://maps.google.com/?q=Malang,+Jawa+Timur",
    external: true,
  },
  {
    icon: iconEnvelope,
    label: "hai@hyred.id",
    href: `/${locale}/contact`,
  },
  {
    icon: iconInstagram,
    label: "@hyred.id",
    href: "https://instagram.com/hyred.id",
    external: true,
  },
];

type FooterContactSectionProps = {
  locale: string;
};

export function FooterContactSection({ locale }: FooterContactSectionProps) {
  const { dictionary } = useI18n();
  const footerCopy = dictionary.common.footer;
  const contactLinks = buildContactLinks(locale);
  const companyLinks = [
    { label: "Blog", href: `/${locale}/blog` },
    { label: "Privacy Policy", href: `/${locale}/privacy` },
    { label: "Terms of Service", href: `/${locale}/terms` },
    { label: "Cookie Policy", href: `/${locale}/cookies` },
    { label: "Contact Us", href: `/${locale}/contact` },
  ];

  const isId = locale === "id";
  // Free tools and articles surfaced for internal linking + crawl depth (per GSC).
  // Tools are kept separate from articles so each cluster reads clearly.
  const freeTools = [
    {
      label: isId ? "Buat CV ATS" : "ATS CV Builder",
      href: `/${locale}/buat-cv-ats`,
    },
    {
      label: isId ? "Review CV Gratis" : "Free CV Review",
      href: `/${locale}/review-cv-gratis`,
    },
    {
      label: isId ? "Cek CV ATS Gratis" : "Free ATS CV Checker",
      href: `/${locale}/cek-cv-ats`,
    },
    {
      label: isId ? "Template CV ATS (.docx)" : "ATS CV Template (.docx)",
      href: `/id/template-cv-ats`,
    },
    {
      label: isId ? "Mock Interview" : "Mock Interview",
      href: `/${locale}/mock-interview-adalah`,
    },
  ];

  const popularArticles = [
    {
      label: isId ? "Cara Cek CV ATS" : "How to Check CV for ATS",
      href: `/${locale}/blog/cara-cek-cv-ats`,
    },
    {
      label: isId ? "Tes Parser CV ATS" : "CV ATS Parser Test",
      href: `/${locale}/blog/test-cv-ats-parser`,
    },
    {
      label: isId ? "Analisis Job Fit" : "Ideal Job Fit",
      href: `/${locale}/blog/ideal-job-fit`,
    },
    // id-only page: pin to /id so the en footer doesn't 308-redirect.
    {
      label: isId ? "Panduan CV ATS" : "ATS CV Guide",
      href: `/id/panduan`,
    },
    // Same id-only pin. Added because the pillar was reachable only from one
    // blog post, which left it at crawl depth 4 with a single incoming link.
    {
      label: isId ? "Optimasi LinkedIn" : "LinkedIn Optimization",
      href: `/id/optimasi-linkedin`,
    },
  ];

  return (
    <footer className="bg-white dark:bg-background px-4 py-12 md:px-8">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <div className="flex w-full max-w-[320px] flex-col gap-2">
          <Link href={`/${locale}`} className="flex items-center">
            <Image
              src="/assets/logo.svg"
              alt="Hyred logo"
              width={136}
              height={42}
              className="h-[42px] w-auto dark:hidden"
            />
            <Image
              src="/assets/dark-logo.svg"
              alt="Hyred logo"
              width={136}
              height={42}
              className="hidden h-[42px] w-auto dark:block"
            />
          </Link>
          <p className="text-[16px] leading-[24px] text-text-heading">
            Help you get hired!
          </p>
        </div>

        <div className="flex w-full flex-col gap-10 md:flex-row md:flex-wrap md:justify-end md:gap-8">
          <div className="flex w-full max-w-[230px] flex-col gap-4">
            <p className="text-[14px] font-medium leading-[20px] text-text-body">
              {isId ? "Alat Gratis" : "Free Tools"}
            </p>
            <div className="flex flex-col gap-3">
              {freeTools.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-[16px] font-semibold leading-[24px] text-text-body"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex w-full max-w-[230px] flex-col gap-4">
            <p className="text-[14px] font-medium leading-[20px] text-text-body">
              {isId ? "Artikel Populer" : "Popular Articles"}
            </p>
            <div className="flex flex-col gap-3">
              {popularArticles.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-[16px] font-semibold leading-[24px] text-text-body"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex w-full max-w-[230px] flex-col gap-4">
            <p className="text-[14px] font-medium leading-[20px] text-text-body">
              Contact Us
            </p>
            <div className="flex flex-col gap-3">
              {contactLinks.map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <Image
                    alt=""
                    src={item.icon}
                    width={18}
                    height={18}
                    aria-hidden="true"
                    className="dark:invert"
                  />
                  <Link
                    href={item.href}
                    className="text-[16px] font-semibold leading-[24px] text-text-body"
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="flex w-full max-w-[230px] flex-col gap-4">
            <p className="text-[14px] font-medium leading-[20px] text-text-body">
              Company
            </p>
            <div className="flex flex-col gap-3">
              {companyLinks.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="text-[16px] font-semibold leading-[24px] text-text-body"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="mt-8 border-t border-border-primary pt-6">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <div className="text-center text-xs text-text-muted">{footerCopy}</div>
          {/* Theme is a set-once preference, so it lives here rather than
              costing the header a next-themes dependency on every page. */}
          <ThemeToggle />
        </div>
      </div>
    </footer>
  );
}
